import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  fetchProfileTemplate,
  patchProfileTemplate,
  searchDataPointsByName,
} from '@/api/companies';
import type { PatchTemplatePayload, TemplateId } from '@/api/network/types';
import {
  useCollaborationStatusOptions,
  useFacilityProcessOptions,
  useNetworkCompanyOptions,
  useProductCategoryOptions,
  useRelationshipTypeOptions,
  useResponsiblePersonOptions,
} from '@/hooks/useDropdowns';
import { COUNTRY_OPTIONS } from '@/lib/constants/countryOptions';

function sanitizeAnswer(
  answer: Record<string, { value: unknown; unit?: string }>,
): Record<string, { value: unknown; unit?: string }> {
  return Object.fromEntries(
    Object.entries(answer).map(([k, entry]) => {
      if ('unit' in entry && (entry as Record<string, unknown>).unit == null) {
        return [k, { value: null }];
      }
      return [k, entry];
    }),
  );
}

const TEMPLATE_IDS: TemplateId[] = [
  'ABOUT',
  'OTHER_IDS',
  'OPERATIONAL_METRICS',
];

/** The ABOUT profile query, shared with readers outside the profile page (the
 *  address banner) so a save here refreshes them from the same cache entry. */
export function aboutProfileQueryOptions(companyId: string) {
  return {
    queryKey: ['network', 'company', companyId, 'profile', 'ABOUT'],
    queryFn: () => fetchProfileTemplate('ABOUT', companyId),
  };
}

export function useGeneralInformation(companyId: string, enabled: boolean) {
  const about = useQuery({
    ...aboutProfileQueryOptions(companyId),
    enabled,
  });

  const otherIds = useQuery({
    queryKey: ['network', 'company', companyId, 'profile', 'OTHER_IDS'],
    queryFn: () => fetchProfileTemplate('OTHER_IDS', companyId),
    enabled,
  });

  const operationalMetrics = useQuery({
    queryKey: [
      'network',
      'company',
      companyId,
      'profile',
      'OPERATIONAL_METRICS',
    ],
    queryFn: () => fetchProfileTemplate('OPERATIONAL_METRICS', companyId),
    enabled,
  });

  const isLoading =
    about.isLoading || otherIds.isLoading || operationalMetrics.isLoading;

  return {
    templates: {
      ABOUT: about.data,
      OTHER_IDS: otherIds.data,
      OPERATIONAL_METRICS: operationalMetrics.data,
    },
    isLoading,
    templateIds: TEMPLATE_IDS,
  };
}

export function useSearchDataPoints(companyId: string, query: string) {
  const [debounced, setDebounced] = useState(query);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  return useQuery({
    queryKey: ['network', 'company', companyId, 'datapoints-search', debounced],
    queryFn: async () => {
      const qs = new URLSearchParams({
        sectionId: '1',
        filter: JSON.stringify({ name: debounced }),
        count: '500',
      });
      const res = await searchDataPointsByName(companyId, qs.toString());
      return res?.data ?? [];
    },
    enabled: debounced.trim().length > 0,
    staleTime: 30_000,
  });
}

export type OptionItem = {
  key?: string;
  id?: string;
  name?: string;
  label?: string;
  text?: string;
};

export interface GeneralInformationOptions {
  [key: string]: OptionItem[] | undefined;
  country: OptionItem[] | undefined;
  facility_processes: OptionItem[] | undefined;
  responsible_person: OptionItem[] | undefined;
  vendors: OptionItem[] | undefined;
  agents: OptionItem[] | undefined;
  collaboration_status: OptionItem[] | undefined;
  collaborationStatus: OptionItem[] | undefined;
  relationship_type: OptionItem[] | undefined;
  relationshipType: OptionItem[] | undefined;
  product_levels: OptionItem[] | undefined;
  productLevels: OptionItem[] | undefined;
  product_types: OptionItem[] | undefined;
  productTypes: OptionItem[] | undefined;
}

export function useGeneralInformationOptions(
  sessionCompanyId: string,
): GeneralInformationOptions {
  const countryOptions = COUNTRY_OPTIONS;
  const { data: facilityProcessOptions } = useFacilityProcessOptions();
  const { data: networkCompanyOptions } = useNetworkCompanyOptions();
  // Responsible persons are users of the logged-in user's own company, not the profile company
  const { data: responsiblePersonOptions } =
    useResponsiblePersonOptions(sessionCompanyId);
  const { data: collaborationStatusOptions } = useCollaborationStatusOptions();
  const { data: relationshipTypeOptions } = useRelationshipTypeOptions();
  const { productLevels, productTypes } = useProductCategoryOptions();

  return {
    country: countryOptions,
    facility_processes: facilityProcessOptions,
    responsible_person: responsiblePersonOptions,
    vendors: networkCompanyOptions,
    agents: networkCompanyOptions,
    collaboration_status: collaborationStatusOptions,
    collaborationStatus: collaborationStatusOptions,
    relationship_type: relationshipTypeOptions,
    relationshipType: relationshipTypeOptions,
    product_levels: productLevels,
    productLevels: productLevels,
    product_types: productTypes,
    productTypes: productTypes,
  };
}

export function usePatchProfileTemplate() {
  return useMutation({
    mutationFn: ({
      templateId,
      payload,
    }: {
      templateId: TemplateId;
      payload: PatchTemplatePayload;
    }) =>
      patchProfileTemplate(templateId, {
        ...payload,
        answer: sanitizeAnswer(payload.answer),
      }),
  });
}
